"use client";
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Flags } from "@/service/feature-flags/flags.service";
import { checkIfFlagExistsForConfig } from "@/lib/utils";

export type IsFeatureFlagEnabledType = (keyName: string, brandId?: string, projectType?: string, step?: string) => boolean;

export const FeatureFlagsContext = createContext<{ featureFlags: Flags } | undefined>(undefined);

export const FeatureFlagsProvider = ({ children, flags }: { children: ReactNode; flags: Flags }) => {
  const [featureFlags, setFeatureFlags] = useState<Flags>(flags);

  useEffect(() => {
    const handleFeatureFlagEvent = (event: CustomEvent) => {
      setFeatureFlags((prevFlags) => ({
        ...prevFlags,
        ...event.detail, // Dynamically update the feature flag
      }));
    };

    // Listen for 'updateFeatureFlag' event
    globalThis.addEventListener("updateFeatureFlag", (event) => handleFeatureFlagEvent(event as CustomEvent));

    return () => {
      globalThis.removeEventListener("updateFeatureFlag", handleFeatureFlagEvent as EventListener);
    };
  }, []);

  const contextValue = useMemo(() => ({ featureFlags }), [featureFlags]);
  return <FeatureFlagsContext.Provider value={contextValue}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlagsContext = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) throw new Error("useFeatureFlags must be used within a FeatureFlagsProvider");

  const isFeatureFlagEnabled: IsFeatureFlagEnabledType = (keyName, brandId, projectType, step): boolean => {
    return checkIfFlagExistsForConfig({
      featureFlags: context.featureFlags,
      keyName,
      brandId: brandId ?? "all",
      projectType,
      step,
    });
  };

  return { isFeatureFlagEnabled };
};
